import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { generateFileUploadSignature, isRequestSigningSupported } from '../utils/requestSigning';
import type { Attribution } from '../utils/track';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed';
export type JobType = 'analysis' | 'conversion' | 'both';
export type Priority = 'low' | 'normal' | 'high';
export type ComplexityLevel = 'low' | 'medium' | 'high';

export interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  status?: string;
  tenantRole: string;
  subscription?: {
    status: string;
    plan: string;
  };
}

export interface AuthResponse {
  user: {
    id: string;
    name?: string | null;
    email: string;
    role: string;
    isActive: boolean;
    emailVerified?: boolean;
    twoFactorEnabled?: boolean;
    createdAt: string;
    // Only present on the login response: true when this is the user's
    // first-ever login. Used (transiently) for the dashboard greeting.
    isFirstLogin?: boolean;
  };
  tenant?: TenantInfo | null;
  token: string;
}

export interface RegisterData {
  email: string;
  password: string;
  organizationName?: string;
  /** Campaign the visitor landed from (OA-137); recorded on the new account by the API. */
  attribution?: Attribution;
}

export interface LoginData {
  email: string;
  password: string;
}

/** Auth method the backend recommends for an email (#218). */
export type AuthMethod = 'password' | 'sso_optional' | 'sso_required';

export interface IdentifyResponse {
  method: AuthMethod;
  ssoAvailable: boolean;
}

export interface SsoStartParams {
  email?: string;
  identityProviderId?: string;
  organizationSlug?: string;
  redirectTo?: string;
}

export interface SsoStartResponse {
  authorizationUrl: string;
}

// ---------------------------------------------------------------------------
// Enterprise Authentication (#219) — domains, identity providers, auth policy,
// break-glass exemptions, diagnostics, and the SSO migration impact report.
// All endpoints are tenant-owner-gated and operate on the caller's own
// organization (no :orgId param).
// ---------------------------------------------------------------------------

export type DomainVerificationStatus = 'pending' | 'verified' | 'failed' | 'revoked';

export interface DomainDnsRecord {
  host: string;
  type: 'TXT';
  value: string;
}

export interface OrganizationDomain {
  organizationDomainId: string;
  domain: string;
  verificationStatus: DomainVerificationStatus;
  verifiedAt: string | null;
  /** Present while the domain is unverified — the TXT record to publish. */
  dnsRecord?: DomainDnsRecord;
}

export type IdentityProviderStatus =
  | 'draft'
  | 'configured'
  | 'test_failed'
  | 'test_successful'
  | 'active'
  | 'disabled';

export interface IdentityProvider {
  identityProviderId: string;
  name: string;
  providerType: string;
  protocol: string;
  status: IdentityProviderStatus;
  clientId: string;
  /** e.g. '••••••••a91f' or null when no secret is stored. */
  clientSecretMask: string | null;
  providerTenantId: string;
  jitEnabled: boolean;
  active: boolean;
  lastTestedAt: string | null;
  lastSuccessfulTestAt: string | null;
  lastSuccessfulLoginAt: string | null;
  redirectUri: string;
}

export interface IdentityProviderInput {
  name?: string;
  clientId: string;
  /** Empty/omitted on PATCH keeps the stored secret. */
  clientSecret?: string;
  providerTenantId: string;
  jitEnabled?: boolean;
}

export type SsoEnforcementMode =
  | 'off'
  | 'optional'
  | 'required_for_verified_domains'
  | 'required_for_all_organization_members'
  | 'required_for_selected_users';

export type DefaultRole = 'owner' | 'manager' | 'member';

export interface AuthPolicy {
  ssoEnforcementMode: SsoEnforcementMode;
  passwordLoginAllowed: boolean;
  jitProvisioningAllowed: boolean;
  defaultRole: DefaultRole;
  breakGlassRequired: boolean;
}

export interface SsoExemptionUser {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

export interface SsoExemption {
  ssoExemptionId: string;
  userId: string;
  reason: string | null;
  expiresAt: string | null;
  active: boolean;
  createdAt: string;
  user: SsoExemptionUser;
}

export interface SsoDiagnosticEntry {
  auditLogId: string;
  action: string;
  result: 'success' | 'failure';
  createdAt: string;
  actorUserId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  metadata?: Record<string, unknown>;
  code?: string;
  hint?: string;
}

export interface SsoImpactReport {
  generatedAt: string;
  verifiedDomains: string[];
  members: {
    total: number;
    alreadyLinked: number;
    willLinkOnNextLogin: number;
    externalDomain: number;
    breakGlass: number;
    detail: Array<Record<string, unknown>>;
  };
  plgUsersOutsideOrganization: number;
  automaticWorkspaceTakeover: false;
}

export interface PDFMetadata {
  title?: string;
  author?: string;
  subject?: string;
  keywords?: string;
  creator?: string;
  producer?: string;
  creationDate?: string;
  modificationDate?: string;
  pageCount?: number;
  version?: string;
  isTagged?: boolean | null;
}

/**
 * Bounding box region for visual highlighting of PDF elements
 */
export interface HighlightRegion {
  x: number;
  y: number;
  width: number;
  height: number;
  pageWidth: number;
  pageHeight: number;
}

/**
 * Example of an issue occurrence with optional highlighting info
 */
export interface IssueExample {
  context: string;
  page: number | null;
  mcid: number | null;
  highlightRegion?: HighlightRegion;
}

export interface PDFAnalysisReport {
  id: string;
  pdfName: string;
  filePath: string;
  fileSize: number;
  processingTime: number;
  createdAt: string;
  conformsTo: string;
  documentType?: 'pdf' | 'docx' | 'xlsx' | 'pptx';
  summary: {
    totalChecks: number;
    passed: number;
    warnings: number;
    errors: number;
    info: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
    // 'untagged' is the analyzer declining to judge: an untagged PDF has no
    // tag tree, so the worker stores this status with a null score, no
    // counters and no issues. The numeric fields above are absent in that
    // case, which is why ReportViewer branches on the status before reading
    // any of them (OA-297).
    overallStatus: 'compliant' | 'partially-compliant' | 'non-compliant' | 'untagged';
    complianceScore: number;
  };
  issues: Array<{
    id: string;
    rule: string;
    status: 'pass' | 'warning' | 'error' | 'info' | 'review';
    severity: 'critical' | 'high' | 'medium' | 'low';
    description?: string;
    location?:
      | string
      | {
          element?: string | null;
          path?: string | null;
          sheetName?: string | null;
          slideIndex?: number | null;
          paragraph?: number | null;
          row?: number | null;
          column?: number | null;
        };
    page?: number;
    mcid?: number | null;
    recommendation?: string;
    standard?: string;
    clause?: string;
    timestamp: string;
    testStatus?: string;
    manualCheckRequired?: boolean;
    occurrenceCount?: number;
    highlightRegions?: HighlightRegion[];
    examples?: IssueExample[];
  }>;
  metadata?: PDFMetadata;
  fileHash?: string;
  screenshot?: {
    url: string;
    width: number;
    height: number;
  };
}

export interface JobResponse {
  message: string;
  jobId: string;
  status: JobStatus;
  statusUrl: string;
  estimatedProcessingTime: string;
  webhookConfigured: boolean;
}

/** Body of POST /api/file/analyze-url (OA-26). */
export interface URLAnalysisRequest {
  url: string;
  priority?: Priority;
  webhookUrl?: string;
}

/** 202 from POST /api/file/analyze-url: the document was downloaded and queued. */
export interface URLAnalysisResponse extends JobResponse {
  /** The URL as stored: credentials and query strings stripped by the API. */
  sourceUrl: string;
  fileSize: number;
}

/**
 * Stable per-cause codes in `error.reason` when `error.code` is
 * `url_download_failed` (officea11y-api docs/url-analysis.md).
 */
export type URLAnalysisFailureReason =
  | 'url_invalid'
  | 'url_not_allowed'
  | 'url_host_unresolvable'
  | 'url_redirect_not_allowed'
  | 'url_too_many_redirects'
  | 'url_timeout'
  | 'url_unreachable'
  | 'url_http_error'
  | 'url_too_large'
  | 'url_unsupported_type'
  | 'url_not_a_document'
  | 'url_domain_rate_limited'
  | 'url_download_error';

/** `error` object in a failed analyze-url response. */
export interface URLAnalysisError {
  code: string;
  message?: string;
  reason?: URLAnalysisFailureReason;
  details?: Array<{ field: string; message: string; value: unknown }>;
}

export interface BatchJobResponse {
  message: string;
  jobs: Array<{
    jobId: string;
    fileName: string;
    statusUrl: string;
  }>;
  webhookConfigured: boolean;
}

export interface JobDetails {
  jobId: string;
  status: JobStatus;
  type: JobType;
  priority: Priority;
  filename: string;
  fileSize: number;
  documentType?: 'pdf' | 'docx' | 'xlsx' | 'pptx';
  created: string;
  updated: string;
  processingStartedAt?: string;
  processingCompletedAt?: string;
  retryCount: number;
  maxRetries: number;
  result?: {
    reportUrl?: string;
    analysisResults?: any;
    metadata?: PDFMetadata;
    fileHash?: string;
    reused?: boolean;
    untagged?: boolean;
    annotatedFileUrl?: string;
  };
  // A failed job's reason. Since api #567 a safe `{ code, message }` (code is
  // one of SERVICE_UNAVAILABLE, PROCESSING_TIMEOUT, INVALID_DOCUMENT,
  // PROCESSING_FAILED), or null when nothing was recorded; older APIs send
  // `{ message }` only. Read it through jobErrorMessage (OA-220).
  error?: {
    code?: string;
    message?: string;
  } | null;
  metadata?: PDFMetadata;
  fileHash?: string;
  thumbnailUrl?: string;
  // True once the retention period has deleted the uploaded document and the
  // files generated from it. The job's report and result remain (OA-146,
  // OA-154). Absent from older API responses.
  filesDeleted?: boolean;
}

export interface JobListResponse {
  jobs: JobDetails[];
  // Mirrors the shape returned by officea11y-api GET /jobs
  // (calculatePagination in src/utils/paginationHelper.js). The page-based
  // fields (totalCount/totalPages/currentPage/hasNext/hasPrevious) drive the
  // History pagination controls (#628).
  pagination: {
    limit: number;
    offset: number;
    totalCount: number;
    totalPages: number;
    currentPage: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

// API Response wrapper types
export interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
  timestamp: string;
}

// Plan types

// Subscription types

// Billing Portal Response

// Payment Method types
export interface PaymentMethod {
  id: string;
  type: string;
  card?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  created: number;
}

// Consent types
export interface ConsentRecord {
  id: string;
  type: string;
  version: string;
  accepted: boolean;
  acceptedAt: string;
  ipAddress?: string;
  userAgent?: string;
}

/**
 * Shape of `GET /cookie-consent/current`. The API returns the stored
 * per-category choices under `categories` (see the API's
 * cookieConsentController.getCurrentConsent) — for an unknown visitor it
 * still returns `categories` with everything but `necessary` false, and
 * `hasConsent: false`.
 */
export interface CurrentConsent {
  hasConsent: boolean;
  consentRequired: boolean;
  consentId?: string;
  categories: {
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
    functional: boolean;
  };
  consentDate?: string;
  expiryDate?: string;
  consentVersion?: string;
  consentMethod?: string;
}

// Payment Intent Status

// Subscription Status

// Discovery Job types
export type DiscoveryJobStatus =
  | 'pending'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface DiscoveryJob {
  discoveryJobId: string;
  title?: string;
  sourceType?: 'web' | 'filesystem' | 'snippet';
  userId: string;
  startUrl: string;
  domain: string;
  scanPath?: string;
  maxDepth: number;
  fileTypes?: string[];
  status: DiscoveryJobStatus;
  pdfsFound: number;
  pagesScanned: number;
  errorCount: number;
  taggedCount?: number;
  // null while a crawl is running or paused: the total page count is unknown,
  // so the API does not invent a percentage (OA-107).
  completionPercentage?: number | null;
  startedAt?: string;
  completedAt?: string;
  lastActivityAt?: string;
  errorMessage?: string;
  /** Safe error code and message (api OA-214); bot_protected on a crawl a site blocked (OA-303). */
  error?: { code: string; message: string } | null;
  autoAnalyze?: boolean;
  monitorEnabled?: boolean;
  lastMonitorRunAt?: string;
  parentJobId?: string;
  fileTypeDistribution?: { type: string; count: number; color: string }[];
  analysisStats?: { tested: number; passed: number };
  createdAt: string;
  updatedAt: string;
}

export interface DiscoveredPdf {
  discoveredPdfId: string;
  discoveryJobId: string;
  pdfUrl: string;
  filePath?: string;
  referrerUrls: string[];
  fileName: string;
  fileSize?: number;
  contentType?: string;
  lastModified?: string;
  isTagged?: boolean;
  pageCount?: number;
  creationDate?: string;
  modificationDate?: string;
  title?: string;
  author?: string;
  subject?: string;
  keywords?: string;
  producer?: string;
  creator?: string;
  pdfVersion?: string;
  metadata?: Record<string, any>;
  discoveryStatus?: 'new' | 'existing' | 'deleted';
  firstDiscoveredAt: string;
  lastSeenAt: string;
  createdAt: string;
  updatedAt: string;
  analysisJobId?: string | null;
  analysisStatus?: string | null;
  reportId?: string | null;
}

export interface CreateDiscoveryJobRequest {
  title?: string;
  startUrl: string;
  domain: string;
  domainPatterns?: string[];
  maxDepth: number;
  fileTypes?: string[];
  autoAnalyze?: boolean;
  monitorEnabled?: boolean;
}

export interface SnippetConfig {
  snippetConfigId: string;
  tenantId: string;
  snippetName: string;
  apiKeyId: string;
  fileTypes: string[];
  autoAnalyze: boolean;
  scanIntervalMins: number;
  sampleRate: number;
  allowedDomains: string[];
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  scriptTag?: string;
  apiKey?: {
    apiKeyId: string;
    keyPrefix: string;
    name: string;
    isActive: boolean;
  };
}

export interface SnippetConfigUpdate {
  apiKeyId: string;
  snippetName?: string;
  fileTypes?: string[];
  autoAnalyze?: boolean;
  scanIntervalMins?: number;
  sampleRate?: number;
  allowedDomains?: string[];
  enabled?: boolean;
}

export interface SnippetConfigCreate extends SnippetConfigUpdate {
  snippetName: string;
}

export interface SnippetConfigListResponse {
  configs: SnippetConfig[];
}

export interface DiscoveryJobListResponse {
  jobs: DiscoveryJob[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
}

export interface DiscoveredPdfListResponse {
  pdfs: DiscoveredPdf[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
}

export interface DiscoveryQueueStats {
  pending: number;
  running: number;
  paused: number;
  completed: number;
  failed: number;
  cancelled: number;
}

// Remediation types
export type RemediationStatus = 'new' | 'in_progress' | 'completed';

export interface RemediationRequest {
  remediationRequestId: string;
  userId: string;
  originalFilename: string;
  fileSize: number;
  pageCount?: number;
  status: RemediationStatus;
  tier: number;
  adminNotes?: string;
  remediatedFilePath?: string;
  assignedAdminId?: string;
  sourceReportId?: string;
  sourceJobId?: string;
  sourceUrl?: string;
  documentComplexity?: 'simple' | 'moderate' | 'complex' | null;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  submitter?: { userId: string; email: string };
  assignedAdmin?: { userId: string; email: string };
}

/**
 * The organization's open remediation request for a document, as a member may
 * see it (GET /remediation/existing, OA-278). A colleague's request is shown
 * by who asked and its status; `canView` says whether the caller may open it.
 */
export interface ExistingRemediation {
  remediationRequestId: string;
  status: string;
  createdAt: string;
  requestedByYou: boolean;
  requestedBy: string | null;
  canView: boolean;
}

export interface RemediationListResponse {
  requests: RemediationRequest[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

// Admin types
export interface AdminUser {
  userId: string;
  email: string;
  role: string;
  isActive: string;
  emailVerified: boolean;
  adminPermissions?: Record<string, string>;
  createdAt: string;
  lastLoginAt?: string;
  // Tenant info (subscription data is now on the tenant)
  tenant?: {
    id: string;
    name: string;
    subscriptionStatus?: string;
    accountTier?: string;
  };
}

export interface AdminUserListResponse {
  users: AdminUser[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface AdminUserDetailResponse {
  user: AdminUser;
  recentActivity: Array<{
    id: string;
    action: string;
    filename?: string;
    createdAt: string;
    metadata?: any;
  }>;
}

export interface AdminStatsResponse {
  system: {
    uptime: number;
    memory: Record<string, number>;
    nodeVersion: string;
    platform: string;
  };
  queue: Record<string, any>;
  cleanup?: Record<string, any>;
  users: {
    total: number;
    last7d: number;
    last30d: number;
    recentSignups: Array<{ email: string; role: string; createdAt: string }>;
  };
  tenants: { total: number };
  jobs: { total: number; last7d: number; failedLast7d: number };
  reports: { total: number };
  remediation: { total: number; open: number };
  discovery: { jobs: number; documents: number };
  webhooks: { active: number };
}

export interface AdminJob {
  jobId: string;
  filename: string;
  originalName?: string;
  fileSize?: number;
  type: string;
  priority: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  retryCount: number;
  maxRetries: number;
  createdAt: string;
  updatedAt: string;
  processingStartedAt?: string | null;
  processingCompletedAt?: string | null;
  errorMessage: string | null;
  userId: string;
  documentType?: string;
}

export interface AdminJobsListResponse {
  jobs: AdminJob[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

/**
 * Axios response-error handler. Extracted so it can be unit-tested
 * against mock error objects without spinning up a full ApiService.
 *
 * Behavior:
 *   - 401 outside login / billing-portal: clears auth and forces /login
 *   - 402 with code=no_funding: stashes the message in sessionStorage
 *     and navigates to /billing so the user can top up or upgrade
 *   - All other errors: pass through unchanged
 */
export function handleApiError(error: any): Promise<never> {
  if (error.response?.status === 401) {
    const url = error.config?.url || '';
    if (url.includes('/auth/login') || url.includes('/stripe/create-billing-portal-session')) {
      return Promise.reject(error);
    }

    // `authToken` is no longer written (#762) but is still removed, so a
    // browser carrying one from before the migration does not keep a readable
    // copy of a token forever.
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    globalThis.location.href = '/login';
  }

  if (
    error.response?.status === 402 &&
    error.response?.data?.error?.code === 'no_funding' &&
    !globalThis.location.pathname.startsWith('/billing')
  ) {
    sessionStorage.setItem(
      'funding-gate-notice',
      error.response.data.error.message ||
        'Your free trial is used up. Add funds or upgrade to continue.'
    );
    globalThis.location.href = '/billing';
  }

  return Promise.reject(error);
}

/** How long to wait for the CSRF token before giving up on it. */
const CSRF_TOKEN_TIMEOUT_MS = 10_000;

/**
 * Routes the API's csrfValidationMiddleware skips outright.
 *
 * Kept in step with officea11y-api's src/middlewares/csrfMiddleware.js — these
 * are unauthenticated by design and carry their own out-of-band credential (an
 * emailed token, an invitation, an origin/apiKey check), so a CSRF token would
 * be checked against nothing.
 *
 * This was `/auth/` as a blanket prefix, which matched the API while the API
 * skipped all of `/api/auth/`. It no longer does: logout, refresh-token,
 * resend-verification and the 2FA management routes run behind requireAuth,
 * and a browser now authenticates them with a cookie it attaches
 * automatically (#762). Those are validated, so listing them here would send
 * no token and collect a 403 on every logout. The unauthenticated auth routes
 * are enumerated instead — one entry per route, matching the API's own list.
 */
const CSRF_EXEMPT_PREFIXES = [
  '/auth/register',
  '/auth/login',
  '/auth/identify',
  '/auth/sso/start',
  '/auth/sso/oidc/callback/',
  '/auth/confirm',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-email/',
  '/auth/two-factor-auth/verify',
  '/public/',
  '/tenants/invitations/accept',
  '/discovery/snippet/ingest',
];

/**
 * A remediation price computed without creating anything -- what the
 * preview-quote endpoints return. complexity is sent back as quotedComplexity
 * when the user confirms (OA-160).
 */
export interface RemediationQuotePreview {
  documentFormat: string;
  pageCount: number;
  complexity: ComplexityLevel;
  ratePerUnit: number;
  complexityMultiplier: number;
  unitLabel: string;
  discountPercent: number;
  totalAmount: number;
}

/** A remediation request comment as the API returns it. */
export interface RemediationCommentView {
  commentId: string;
  body: string;
  isInternal: boolean;
  isSystemMessage: boolean;
  createdAt: string;
  author: { userId: string; email: string; isStaff: boolean };
}

/** Which side of the remediation API a call is made from. */
type RemediationAudience = 'customer' | 'admin';

/** Path to one remediation request, on the customer or the admin routes. */
const remediationPath = (audience: RemediationAudience, requestId: string): string =>
  audience === 'admin' ? `/remediation/admin/${requestId}` : `/remediation/${requestId}`;

class ApiService {
  private api: AxiosInstance;

  /**
   * Cached CSRF token, paired with the API's `csrfToken` cookie.
   *
   * The cookie is HttpOnly, so it cannot be read back out of `document.cookie`
   * — `GET /api/csrf-token` is the only way to learn the value the server will
   * compare against.
   */
  private csrfToken: string | null = null;

  /**
   * The in-flight token fetch, so concurrent callers share one request.
   *
   * Without this, several unauthenticated writes issued at once each fetch
   * their own token. They recover — a loser's token is stale and the retry
   * refreshes it — but the round-trips are wasted and the recovery shows up as
   * a 403 in the network log, which reads like a fault.
   */
  private csrfTokenRequest: Promise<string | null> | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      // The session is an HttpOnly cookie the API sets at login (#762). It is
      // unreadable from script by design, so nothing here attaches it — the
      // browser does, and only if the request is credentialed.
      withCredentials: true,
    });

    this.api.interceptors.request.use(async config => {
      if (ApiService.needsCsrfToken(config)) {
        const csrfToken = await this.getCsrfToken();
        if (csrfToken) {
          config.headers['X-CSRF-Token'] = csrfToken;
        }
      }

      return config;
    });

    // Add response interceptor to handle errors
    this.api.interceptors.response.use(
      response => response,
      error => this.retryOnStaleCsrfToken(error)
    );
  }

  /**
   * Whether a request has to carry a CSRF token.
   *
   * Mirrors what the API's csrfValidationMiddleware actually validates, so the
   * token is fetched only when it will be checked. The middleware skips safe
   * methods and the routes in CSRF_EXEMPT_PREFIXES below; every other write
   * from this app is validated.
   *
   * There is no exemption for an `Authorization` header any more. Until
   * officea11y-api #563 (OA-209) the API skipped any request carrying
   * `Authorization` or `X-API-Key`, and this mirrored it. The API now exempts a
   * header credential only when no session cookie is sent: its auth layer reads
   * the cookie ahead of the bearer header, so a forged request with the
   * victim's cookie plus any header used to be authenticated by the cookie and
   * skip CSRF. This app always sends the session cookie (`withCredentials`,
   * #762), so a write that set `Authorization` would now be validated -- and,
   * without a token, refused 403. Every mutating request carries the token.
   *
   * Matching that list matters for more than tidiness: fetching a token costs a
   * round-trip, and a broader rule put one in front of every unauthenticated
   * POST — including `/auth/login`, on the app's most latency-sensitive path,
   * for a token the endpoint never reads.
   *
   * What remains is the genuinely protected set. `PUT /cookie-consent/update`
   * is the one the app makes today: an anonymous visitor's consent record is
   * addressed by a signed cookie, which is a real cookie-borne credential, so
   * the protection is worth satisfying rather than exempting server-side.
   */
  private static needsCsrfToken(config: InternalAxiosRequestConfig): boolean {
    const method = (config.method ?? 'get').toUpperCase();
    if (['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      return false;
    }

    // baseURL already carries the `/api` prefix, so match on the path below it.
    const url = config.url ?? '';
    return !CSRF_EXEMPT_PREFIXES.some(prefix => url.startsWith(prefix));
  }

  /**
   * Fetch the CSRF token the server will compare against, and cache it.
   *
   * Deliberately uses a bare axios call rather than `this.api`: routing it
   * through the instance would re-enter the request interceptor. Credentials
   * are required both ways — the response sets the paired cookie when none
   * exists yet, and the cookie must accompany the request that uses the token.
   */
  private async getCsrfToken(): Promise<string | null> {
    if (this.csrfToken) {
      return this.csrfToken;
    }

    this.csrfTokenRequest ??= this.fetchCsrfToken();

    try {
      return await this.csrfTokenRequest;
    } finally {
      this.csrfTokenRequest = null;
    }
  }

  /**
   * Ask the API for the token paired with its cookie.
   *
   * Bounded by a timeout: the axios instance sets none, so without one a
   * hanging /csrf-token would stall the request that needs it indefinitely —
   * and this sits in front of a user-visible save.
   */
  private async fetchCsrfToken(): Promise<string | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/csrf-token`, {
        withCredentials: true,
        timeout: CSRF_TOKEN_TIMEOUT_MS,
      });
      this.csrfToken = response.data?.data?.csrfToken ?? null;
    } catch {
      // Leave the token unset; the request proceeds and the API decides.
      this.csrfToken = null;
    }

    return this.csrfToken;
  }

  /**
   * Retry once with a fresh token when the cached one is no longer valid.
   *
   * The cookie lasts 24 hours, so a long-lived tab can outlive it. Without
   * this the next consent change fails silently — the same class of failure
   * this whole path is fixing.
   */
  private async retryOnStaleCsrfToken(error: any): Promise<never> {
    // The API sends { error: { code: 'csrf_token_invalid' } } (OA-144). Older
    // builds sent only a top-level code: 'CSRF_TOKEN_INVALID'; the API keeps
    // that copy for one release, and the UI may deploy first, so read both.
    const data = error.response?.data;
    const isCsrfFailure =
      error.response?.status === 403 &&
      (data?.error?.code === 'csrf_token_invalid' || data?.code === 'CSRF_TOKEN_INVALID');

    if (isCsrfFailure && error.config && !error.config.__csrfRetried) {
      this.csrfToken = null;
      const csrfToken = await this.getCsrfToken();

      if (csrfToken) {
        error.config.__csrfRetried = true;
        error.config.headers['X-CSRF-Token'] = csrfToken;
        return this.api.request(error.config);
      }
    }

    return handleApiError(error);
  }

  // Authentication endpoints
  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await this.api.post('/auth/register', data);
    // Server wraps in successResponse: { success, message, data: { user, tenant, token } }
    const payload = response.data?.data || response.data;
    return payload;
  }

  async login(data: LoginData): Promise<AuthResponse> {
    const response = await this.api.post('/auth/login', data);
    // Server returns { user, tenant, token } directly (or { requires2FA, ... })
    return response.data;
  }

  /**
   * Email-first login step 1 (#218). Tells the UI which authentication method
   * to offer for a given email WITHOUT revealing whether the account exists.
   *   - method 'password'      → show the password form
   *   - method 'sso_optional'  → offer SSO and the password form
   *   - method 'sso_required'  → SSO only
   */
  async identify(email: string): Promise<IdentifyResponse> {
    const response = await this.api.post('/auth/identify', { email });
    return response.data?.data || response.data;
  }

  /**
   * Begin an SSO login (#218). Resolve a provider by email, identity provider
   * id, or organization slug; the caller must full-page redirect the browser
   * to the returned authorizationUrl. A 404 with code 'sso_not_available'
   * means no SSO is configured for the given input.
   */
  async ssoStart(params: SsoStartParams): Promise<SsoStartResponse> {
    const response = await this.api.post('/auth/sso/start', params);
    return response.data?.data || response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/auth/logout');
    } finally {
      // Always forget the cached profile, even if the API call failed.
      //
      // Note the asymmetry this introduces (#762): only the API can clear the
      // HttpOnly cookie, so if POST /auth/logout did not reach it, the session
      // is still live server-side even though this browser now looks logged
      // out. That is strictly better than the old behaviour -- where the
      // client dropped the only copy of the token and the session stayed valid
      // until it expired, unrevoked -- but it is not the same as a logout
      // having happened.
      this.clearStoredSession();
    }
  }

  async getProfile(): Promise<AuthResponse['user']> {
    const response: AxiosResponse<AuthResponse['user']> = await this.api.get('/auth/profile');
    return response.data;
  }

  async logoutAll(): Promise<{ message: string; count?: number }> {
    const response = await this.api.post('/auth/logout-all');
    return response.data;
  }

  async resendVerificationEmail(): Promise<{ message: string }> {
    const response = await this.api.post('/auth/resend-verification');
    return response.data;
  }

  async changePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<{ message: string }> {
    const response = await this.api.post('/account/change-password', {
      currentPassword,
      newPassword,
      confirmPassword,
    });
    return response.data;
  }

  // Two-Factor Authentication methods
  async setup2FA(): Promise<{ secret: string; qrCode: string; backupCodes: string[] }> {
    const response = await this.api.post('/auth/two-factor-auth/setup');
    return response.data.data || response.data;
  }

  async enable2FA(token: string): Promise<{ message: string; backupCodes: string[] }> {
    const response = await this.api.post('/auth/two-factor-auth/enable', { token });
    const body = response.data;
    return { message: body.message, ...body.data };
  }

  async disable2FA(token: string): Promise<{ message: string }> {
    const response = await this.api.post('/auth/two-factor-auth/disable', { token });
    return { message: response.data.message, ...response.data.data };
  }

  /**
   * Second login step. `challengeToken` comes from the login response and proves
   * the password was checked; the API takes the user from it (OA-150).
   */
  async verify2FA(challengeToken: string, token: string): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post(
      '/auth/two-factor-auth/verify',
      {
        challengeToken,
        token,
      }
    );
    return response.data;
  }

  async regenerateBackupCodes(token: string): Promise<{ message: string; backupCodes: string[] }> {
    const response = await this.api.post('/auth/two-factor-auth/regenerate-backup-codes', {
      token,
    });
    const body = response.data;
    return { message: body.message, ...body.data };
  }

  // Tenant / Organization management
  async getCurrentTenant(): Promise<{ tenant: TenantInfo }> {
    const response = await this.api.get('/tenants/current');
    return response.data;
  }

  async updateTenant(updates: {
    name?: string;
    slug?: string;
    domain?: string;
    settings?: Record<string, unknown>;
  }): Promise<{ tenant: TenantInfo }> {
    const response = await this.api.put('/tenants/current', updates);
    return response.data;
  }

  async getTenantMembers(params?: { limit?: number; offset?: number }): Promise<{
    members: Array<{
      userId: string;
      email: string;
      tenantRole: string;
      status: string;
      joinedAt: string;
      invitedAt?: string;
    }>;
    pagination: { total: number; limit: number; offset: number };
  }> {
    const response = await this.api.get('/tenants/current/members', { params });
    return response.data;
  }

  async inviteMember(data: { email: string; tenantRole?: string }): Promise<{
    invitation: {
      userId: string;
      email: string;
      tenantRole: string;
      status: string;
      inviteToken: string;
    };
  }> {
    const response = await this.api.post('/tenants/current/invite', data);
    return response.data;
  }

  async updateMemberRole(
    userId: string,
    tenantRole: string
  ): Promise<{ member: { userId: string; tenantRole: string } }> {
    const response = await this.api.put(`/tenants/current/members/${userId}/role`, { tenantRole });
    return response.data;
  }

  // Hand the organization to another active member; the caller becomes a
  // manager (OA-163). Owner only, and refused for an API key. The owner must
  // re-authenticate (OA-246): their current password, or an SSO sign-in in
  // the last ten minutes, in which case no password is sent.
  async transferOwnership(
    userId: string,
    currentPassword?: string
  ): Promise<{
    owner: { userId: string; email: string };
    tenantRole: string;
  }> {
    const response = await this.api.post('/tenants/current/transfer-ownership', {
      userId,
      ...(currentPassword ? { currentPassword } : {}),
    });
    return response.data;
  }

  async removeMember(userId: string): Promise<{ removed: boolean }> {
    const response = await this.api.delete(`/tenants/current/members/${userId}`);
    return response.data;
  }

  async resendInvitation(userId: string): Promise<{ message: string }> {
    const response = await this.api.post(`/tenants/current/members/${userId}/resend-invitation`);
    return response.data;
  }

  async acceptInvitation(data: { token: string; password: string }): Promise<{
    user: { id: string; email: string };
    tenant: { id: string; name: string; slug: string };
    tenantRole: string;
  }> {
    const response = await this.api.post('/tenants/invitations/accept', data);
    return response.data;
  }

  // Email verification and password reset
  async verifyEmail(token: string): Promise<{ message: string }> {
    const response = await this.api.get(`/auth/verify-email/${token}`);
    return response.data;
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await this.api.post('/auth/forgot-password', { email });
    return response.data;
  }

  // Public contact form (#701). Unauthenticated; the axios instance only adds
  // an auth token when one is present, so this works for logged-out visitors.
  //
  // `withCredentials` is required, not optional. /contact is not on the API's
  // CSRF skip list, so csrfValidationMiddleware compares the X-CSRF-Token
  // header against the `csrfToken` cookie. The request interceptor supplies the
  // header, but the axios instance sends no cookies by default -- so without
  // this the header arrives with nothing to match and the API returns 403,
  // which the form surfaces as "Sorry, we couldn't send your message."
  //
  // Verified against the running API: header alone -> 403, header + cookie ->
  // 200. Same reason the cookie-consent calls below carry it.
  async submitContactForm(data: {
    name: string;
    email: string;
    subject?: string;
    message: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post('/contact', data, { withCredentials: true });
    return response.data;
  }

  async resetPassword(
    token: string,
    password: string,
    confirmPassword: string
  ): Promise<{ message: string }> {
    const response = await this.api.post('/auth/reset-password', {
      token,
      password,
      confirmPassword,
    });
    return response.data;
  }

  // PDF analysis endpoints (async)
  async analyzePDF(
    file: File,
    options: {
      webhookUrl?: string;
      priority?: Priority;
    } = {}
  ): Promise<JobResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (options.webhookUrl) {
      formData.append('webhookUrl', options.webhookUrl);
    }
    if (options.priority) {
      formData.append('priority', options.priority);
    }

    // Generate request signature if supported
    const headers: Record<string, string> = {
      'Content-Type': 'multipart/form-data',
    };

    if (isRequestSigningSupported()) {
      try {
        // Get current user ID from local storage
        const userStr = localStorage.getItem('user');
        const user = userStr ? (JSON.parse(userStr) as { id?: string } | null) : null;

        if (user?.id) {
          const signature = await generateFileUploadSignature(file, user.id);
          headers['X-Upload-Signature'] = signature;
        }
      } catch (error) {
        console.warn('Failed to generate upload signature:', error);
        // Continue without signature - backend supports optional signing
      }
    }

    const response: AxiosResponse<JobResponse> = await this.api.post('/file/analyze', formData, {
      headers,
    });
    return response.data;
  }

  async analyzeBatchPDF(
    files: File[],
    options: {
      webhookUrl?: string;
      priority?: Priority;
    } = {}
  ): Promise<BatchJobResponse> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    if (options.webhookUrl) {
      formData.append('webhookUrl', options.webhookUrl);
    }
    if (options.priority) {
      formData.append('priority', options.priority);
    }

    const headers: Record<string, string> = {
      'Content-Type': 'multipart/form-data',
    };

    const response: AxiosResponse<BatchJobResponse> = await this.api.post(
      '/file/analyze-batch',
      formData,
      { headers }
    );
    return response.data;
  }

  async getReport(reportId: string): Promise<PDFAnalysisReport> {
    const response: AxiosResponse<PDFAnalysisReport> = await this.api.get(
      `/file/reports/${reportId}`
    );
    return response.data;
  }

  async getOriginalPdf(reportId: string): Promise<Blob> {
    const response = await this.api.get(`/file/reports/${reportId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Admin report view, opened from the admin remediation detail page. These go
  // through /file/admin/reports/*, which the API resolves without the admin's
  // own organisation membership; the customer routes above scope such an
  // admin to their own organisation and answer 404 for anyone else's report
  // (OA-164, as OA-149 was for the request itself).
  async getAdminReport(reportId: string): Promise<PDFAnalysisReport> {
    const response: AxiosResponse<PDFAnalysisReport> = await this.api.get(
      `/file/admin/reports/${reportId}`
    );
    return response.data;
  }

  async getAdminOriginalPdf(reportId: string): Promise<Blob> {
    const response = await this.api.get(`/file/admin/reports/${reportId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Job tracking endpoints
  async getJobStatus(jobId: string): Promise<JobDetails> {
    const response: AxiosResponse<JobDetails> = await this.api.get(`/jobs/${jobId}`);
    return response.data;
  }

  async getUserJobs(
    filters: {
      status?: JobStatus;
      type?: JobType;
      documentType?: string;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<JobListResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value.toString());
      }
    });

    // Always expand result data to get metadata
    params.append('expand', 'result');

    const response: AxiosResponse<JobListResponse> = await this.api.get(
      `/jobs?${params.toString()}`
    );
    return response.data;
  }

  async retryJob(jobId: string): Promise<{ message: string; jobId: string }> {
    const response = await this.api.post(`/jobs/${jobId}/retry`);
    return response.data;
  }

  async deleteJob(jobId: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/jobs/${jobId}`);
    return response.data;
  }

  async getActivityLogs(
    filters: {
      page?: number;
      limit?: number;
      action?: string;
    } = {}
  ): Promise<{
    logs: Array<{
      id: string;
      action: string;
      filename: string;
      pageCount?: number;
      timestamp: string;
      metadata?: any;
      jobId?: string;
      reportId?: string;
      canViewReport?: boolean;
    }>;
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value.toString());
      }
    });

    const response = await this.api.get(`/logs?${params.toString()}`);
    return response.data;
  }

  /**
   * Forget the locally cached profile.
   *
   * There is no token to remove any more — the session is an HttpOnly cookie
   * only the API can clear, which `POST /auth/logout` does (#762). What lives
   * here is the cached `user`/`tenant` the app renders from before the profile
   * fetch returns, and dropping it is what makes the UI look logged out
   * immediately rather than a round-trip later.
   *
   * Called on logout and whenever a 401 says the session is gone. It is not a
   * substitute for logout: on its own the cookie survives, which is the point
   * of the cookie.
   */
  clearStoredSession(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
  }

  // Stripe payment method management (kept — used by new billing system)
  async createSetupIntent(): Promise<{ client_secret: string }> {
    const response = await this.api.post('/stripe/create-setup-intent');
    return response.data.data || response.data;
  }

  async getPaymentMethods(): Promise<PaymentMethod[]> {
    const response =
      await this.api.get<ApiSuccessResponse<PaymentMethod[]>>('/stripe/payment-methods');
    return response.data.data || [];
  }

  async deletePaymentMethod(paymentMethodId: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/stripe/payment-methods/${paymentMethodId}`);
    return response.data;
  }

  // Account management endpoints
  /**
   * An email change needs `currentPassword` and is only staged: the API mails a
   * confirmation link to the new address and keeps the old one until it is
   * followed (OA-145).
   */
  async updateProfile(data: {
    name?: string;
    email?: string;
    currentPassword?: string;
  }): Promise<{ message: string }> {
    const response = await this.api.put('/account/profile', data);
    return response.data;
  }

  /** Apply a pending email change with the token from the confirmation link (OA-145). */
  async confirmEmailChange(token: string): Promise<{ data: { email: string }; message: string }> {
    const response = await this.api.post('/auth/confirm-email-change', { token });
    return response.data;
  }

  // API Key management
  async createApiKey(data: { name: string; expiresAt?: string }): Promise<{
    apiKeyId: string;
    name: string;
    key: string;
    keyPrefix: string;
    expiresAt: string | null;
    createdAt: string;
  }> {
    const response = await this.api.post('/account/api-keys', data);
    return response.data;
  }

  async listApiKeys(): Promise<{
    keys: Array<{
      apiKeyId: string;
      name: string;
      keyPrefix: string;
      lastUsedAt: string | null;
      expiresAt: string | null;
      isActive: boolean;
      createdAt: string;
    }>;
  }> {
    const response = await this.api.get('/account/api-keys');
    return response.data;
  }

  async revokeApiKey(apiKeyId: string): Promise<{ apiKeyId: string; revoked: boolean }> {
    const response = await this.api.patch(`/account/api-keys/${apiKeyId}/revoke`);
    return response.data;
  }

  async deleteApiKey(apiKeyId: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/account/api-keys/${apiKeyId}`);
    return response.data;
  }

  async deleteAccount(currentPassword: string): Promise<{ message: string }> {
    // DELETE /api/account, with the password the API requires (OA-156). The UI
    // used to POST to /account/delete, which has no route.
    const response = await this.api.delete('/account', { data: { currentPassword } });
    return response.data;
  }

  async requestDataExport(): Promise<{ downloadUrl: string }> {
    const response = await this.api.post('/gdpr/export-data');
    return response.data;
  }

  async exportAccountData(): Promise<any> {
    const response = await this.api.get('/account/export');
    return response.data?.data ?? response.data;
  }

  // GDPR consent endpoints
  //
  // These three carry `withCredentials` because an anonymous visitor's consent
  // record is addressed by a signed, HttpOnly cookie the API mints on write
  // (#319). Without it the browser neither stores nor returns that cookie
  // cross-origin, so every anonymous save minted a *fresh* subject that was
  // never sent back — an anonymous visitor could record a decision but could
  // never see or change it afterwards (#322).
  //
  // Scoped to these calls rather than set on the axios instance: authentication
  // here is a Bearer token from localStorage, not a cookie, so sending
  // credentials on every request would widen the CSRF surface for no benefit.
  // The API's CORS allows credentials against an explicit origin allowlist.
  async getCurrentConsent(): Promise<CurrentConsent | null> {
    const response = await this.api.get<ApiSuccessResponse<CurrentConsent | null>>(
      '/cookie-consent/current',
      { withCredentials: true }
    );
    return response.data.data;
  }

  async getConsentHistory(): Promise<ConsentRecord[]> {
    const response = await this.api.get<ApiSuccessResponse<ConsentRecord[]>>(
      '/cookie-consent/history',
      { withCredentials: true }
    );
    return response.data.data;
  }

  // PUT, not POST: the route is `router.put('/update', ...)` in the API's
  // cookie-consent-routes.js. Express matches on method as well as path, so a
  // POST here 404s and the save silently fails (#751).
  async updateConsent(preferences: Record<string, boolean>): Promise<{ message: string }> {
    const response = await this.api.put('/cookie-consent/update', preferences, {
      withCredentials: true,
    });
    return response.data;
  }

  // HTML conversion endpoints
  async convertPdfToHtml(file: File): Promise<{
    message: string;
    conversion: {
      originalFileName: string;
      htmlFileName: string;
      // htmlFilePath/htmlFolderPath (absolute server paths) are no longer
      // returned; address the output by htmlFileName (OA-188).
      htmlSize: number;
      convertedAt: string;
    };
    access: {
      downloadUrl: string;
      viewUrl: string;
    };
    htmlContent?: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('saveContent', 'false'); // Don't include content in response for UI

    // Generate request signature if supported
    const headers: Record<string, string> = {
      'Content-Type': 'multipart/form-data',
    };

    if (isRequestSigningSupported()) {
      try {
        // Get current user ID from local storage
        const userStr = localStorage.getItem('user');
        const user = userStr ? (JSON.parse(userStr) as { id?: string } | null) : null;

        if (user?.id) {
          const signature = await generateFileUploadSignature(file, user.id);
          headers['X-Upload-Signature'] = signature;
        }
      } catch (error) {
        console.warn('Failed to generate upload signature:', error);
        // Continue without signature - backend supports optional signing
      }
    }

    const response = await this.api.post('/html/convert', formData, {
      headers,
    });
    return response.data;
  }

  async getHtmlFilesList(): Promise<{
    files: Array<{
      filename: string;
      folder: string;
      size: number;
      createdAt: string;
      modifiedAt: string;
      downloadUrl: string;
      viewUrl: string;
    }>;
    totalCount: number;
  }> {
    const response = await this.api.get('/html/list');
    return response.data;
  }

  async downloadHtmlFile(filename: string): Promise<Blob> {
    const response = await this.api.get(`/html/download/${encodeURIComponent(filename)}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async viewHtmlFile(filename: string): Promise<string> {
    const response = await this.api.get(`/html/view/${encodeURIComponent(filename)}`, {
      responseType: 'text',
    });
    return response.data;
  }

  async downloadHtmlZip(filename: string): Promise<Blob> {
    const response = await this.api.get(`/html/download-zip/${encodeURIComponent(filename)}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Word conversion endpoints
  async convertPdfToWord(file: File): Promise<{ message: string; jobId: string }> {
    const formData = new FormData();
    formData.append('file', file);
    // Issue #524: do NOT set Content-Type manually. Setting it to
    // 'multipart/form-data' without a boundary causes the gateway to
    // reject the body, which surfaces upstream as a 422. Let axios /
    // the browser pick the header with the correct boundary.
    const response = await this.api.post('/conversion/pdf-to-word', formData);
    return response.data;
  }

  async downloadWordDocument(jobId: string): Promise<Blob> {
    const response = await this.api.get(`/conversion/download-word/${jobId}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Generic document conversion
  async getConversionFormats(): Promise<{
    matrix: Record<
      string,
      Array<{
        id: string;
        name: string;
        extension: string;
        mimeType: string;
        tool: string;
        description: string;
      }>
    >;
    toolAvailability: Record<string, boolean>;
  }> {
    const response = await this.api.get('/conversion/formats');
    return response.data;
  }

  async convertDocument(
    file: File,
    outputFormat: string
  ): Promise<{ message: string; jobId: string; outputFormat: string; outputName: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('outputFormat', outputFormat);

    const response = await this.api.post('/conversion/convert', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async downloadConvertedDocument(jobId: string): Promise<Blob> {
    const response = await this.api.get(`/conversion/download/${jobId}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // ============================================================================
  // Discovery API Methods
  // ============================================================================

  /**
   * Create a new discovery job
   */
  async createDiscoveryJob(data: CreateDiscoveryJobRequest): Promise<DiscoveryJob> {
    const response: AxiosResponse<{ message: string; job: DiscoveryJob }> = await this.api.post(
      '/discovery/jobs',
      data
    );
    return response.data.job;
  }

  /**
   * Get list of discovery jobs
   */
  async getDiscoveryJobs(
    filters: {
      status?: DiscoveryJobStatus;
      limit?: number;
      offset?: number;
      sort?: string;
    } = {}
  ): Promise<DiscoveryJobListResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value.toString());
      }
    });

    const response: AxiosResponse<DiscoveryJobListResponse> = await this.api.get(
      `/discovery/jobs?${params.toString()}`
    );
    return response.data;
  }

  /**
   * Get a specific discovery job
   */
  async getDiscoveryJob(jobId: string): Promise<DiscoveryJob> {
    const response: AxiosResponse<{ job: DiscoveryJob }> = await this.api.get(
      `/discovery/jobs/${jobId}`
    );
    return response.data.job;
  }

  /**
   * Pause a running discovery job
   */
  async pauseDiscoveryJob(jobId: string): Promise<DiscoveryJob> {
    const response: AxiosResponse<DiscoveryJob> = await this.api.post(
      `/discovery/jobs/${jobId}/pause`
    );
    return response.data;
  }

  /**
   * Resume a paused discovery job
   */
  async resumeDiscoveryJob(jobId: string): Promise<DiscoveryJob> {
    const response: AxiosResponse<DiscoveryJob> = await this.api.post(
      `/discovery/jobs/${jobId}/resume`
    );
    return response.data;
  }

  /**
   * Cancel a discovery job
   */
  async cancelDiscoveryJob(jobId: string): Promise<DiscoveryJob> {
    const response: AxiosResponse<DiscoveryJob> = await this.api.post(
      `/discovery/jobs/${jobId}/cancel`
    );
    return response.data;
  }

  /**
   * Delete a discovery job
   */
  async deleteDiscoveryJob(jobId: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/discovery/jobs/${jobId}`);
    return response.data;
  }

  /**
   * Get PDFs discovered by a job
   */
  async getDiscoveredPdfs(
    jobId: string,
    filters: {
      isTagged?: boolean;
      limit?: number;
      offset?: number;
      sort?: string;
    } = {}
  ): Promise<DiscoveredPdfListResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value.toString());
      }
    });

    const response: AxiosResponse<DiscoveredPdfListResponse> = await this.api.get(
      `/discovery/jobs/${jobId}/pdfs?${params.toString()}`
    );
    return response.data;
  }

  /**
   * Get queue statistics
   */
  async getDiscoveryQueueStats(): Promise<DiscoveryQueueStats> {
    const response: AxiosResponse<DiscoveryQueueStats> = await this.api.get('/discovery/stats');
    return response.data;
  }

  /**
   * Export discovered PDFs as CSV
   */
  async exportDiscoveredPdfs(jobId: string): Promise<Blob> {
    const response = await this.api.get(`/discovery/jobs/${jobId}/export`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Analyze a document by URL (discovered documents and the analyzer's URL tab)
   */
  async analyzePdfByUrl(url: string): Promise<URLAnalysisResponse> {
    // Ensure URL is properly encoded (discovered URLs may have unencoded spaces)
    const encodedUrl = new URL(url).href;
    const body: URLAnalysisRequest = { url: encodedUrl };
    const response: AxiosResponse<URLAnalysisResponse> = await this.api.post(
      '/file/analyze-url',
      body
    );
    return response.data;
  }

  /**
   * Analyze a discovered document by its ID (for fs-crawler discovered documents)
   */
  async analyzeDiscoveredPdf(discoveredPdfId: string): Promise<JobResponse> {
    const response: AxiosResponse<JobResponse> = await this.api.post('/file/analyze-discovered', {
      discoveredPdfId,
    });
    return response.data;
  }

  async toggleDiscoveryMonitor(
    jobId: string,
    enabled: boolean
  ): Promise<{ message: string; monitorEnabled: boolean }> {
    const response = await this.api.put(`/discovery/jobs/${jobId}/monitor`, { enabled });
    return response.data;
  }

  async updateDiscoveryJobParams(
    jobId: string,
    params: { startUrl?: string; domain?: string; maxDepth?: number; autoAnalyze?: boolean }
  ): Promise<{ message: string; job: Partial<DiscoveryJob> }> {
    const response = await this.api.patch(`/discovery/jobs/${jobId}`, params);
    return response.data;
  }

  async rerunDiscoveryJob(
    jobId: string,
    overrides?: Partial<CreateDiscoveryJobRequest>
  ): Promise<{ message: string; job: DiscoveryJob }> {
    const response = await this.api.post(`/discovery/jobs/${jobId}/rerun`, overrides || {});
    return response.data;
  }

  // ============================================================================
  // Snippet Discovery API Methods
  // ============================================================================

  async getSnippetConfig(): Promise<SnippetConfig> {
    const response: AxiosResponse<SnippetConfig> = await this.api.get('/discovery/snippet/config');
    return response.data;
  }

  async updateSnippetConfig(config: SnippetConfigUpdate): Promise<SnippetConfig> {
    const response: AxiosResponse<SnippetConfig> = await this.api.put(
      '/discovery/snippet/config',
      config
    );
    return response.data;
  }

  async deleteSnippetConfig(): Promise<{ message: string }> {
    const response = await this.api.delete('/discovery/snippet/config');
    return response.data;
  }

  // Multiple named snippet configs per tenant (#635).
  async listSnippetConfigs(): Promise<SnippetConfig[]> {
    const response: AxiosResponse<SnippetConfigListResponse> = await this.api.get(
      '/discovery/snippet/configs'
    );
    return response.data.configs;
  }

  async createSnippetConfig(config: SnippetConfigCreate): Promise<SnippetConfig> {
    const response: AxiosResponse<SnippetConfig> = await this.api.post(
      '/discovery/snippet/configs',
      config
    );
    return response.data;
  }

  async getSnippetConfigById(configId: string): Promise<SnippetConfig> {
    const response: AxiosResponse<SnippetConfig> = await this.api.get(
      `/discovery/snippet/configs/${configId}`
    );
    return response.data;
  }

  async updateSnippetConfigById(
    configId: string,
    config: SnippetConfigUpdate
  ): Promise<SnippetConfig> {
    const response: AxiosResponse<SnippetConfig> = await this.api.put(
      `/discovery/snippet/configs/${configId}`,
      config
    );
    return response.data;
  }

  async deleteSnippetConfigById(configId: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/discovery/snippet/configs/${configId}`);
    return response.data;
  }

  // ============================================================================
  // Remediation API Methods
  // ============================================================================

  async createRemediationRequest(
    file: File,
    options: {
      sourceReportId?: string;
      sourceUrl?: string;
      quotedComplexity?: ComplexityLevel;
      quotedAmount?: number;
    } = {}
  ): Promise<{ message: string; request: RemediationRequest }> {
    const formData = new FormData();
    formData.append('file', file);
    if (options.sourceReportId) {
      formData.append('sourceReportId', options.sourceReportId);
    }
    if (options.sourceUrl) {
      formData.append('sourceUrl', options.sourceUrl);
    }
    if (options.quotedComplexity) {
      formData.append('quotedComplexity', options.quotedComplexity);
    }
    if (typeof options.quotedAmount === 'number') {
      formData.append('quotedAmount', String(options.quotedAmount));
    }

    const response = await this.api.post('/remediation', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  /**
   * Preview a remediation quote — used by the upload-time confirm dialog
   * on /remediation. Returns complexity + page count + total without
   * persisting anything.
   */
  async previewRemediationQuote(file: File): Promise<{
    quote: {
      documentFormat: string;
      pageCount: number;
      complexity: ComplexityLevel;
      ratePerUnit: number;
      complexityMultiplier: number;
      unitLabel: string;
      discountPercent: number;
      totalAmount: number;
    };
  }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post('/remediation/preview-quote', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // Preview the remediation quote for an existing analysis report — drives
  // the report-detail "Request Remediation" cost-confirm dialog so it has
  // parity with the upload-time quote on /remediation. (#616)
  async previewRemediationQuoteFromReport(reportId: string): Promise<{
    quote: {
      documentFormat: string;
      pageCount: number;
      complexity: ComplexityLevel;
      ratePerUnit: number;
      complexityMultiplier: number;
      unitLabel: string;
      discountPercent: number;
      totalAmount: number;
    };
  }> {
    const response = await this.api.post('/remediation/preview-quote-from-report', {
      reportId,
    });
    return response.data;
  }

  async createRemediationRequestFromReport(
    reportId: string,
    quote?: { quotedComplexity?: string; quotedAmount?: number }
  ): Promise<{ message: string; request: RemediationRequest }> {
    const response = await this.api.post('/remediation/from-report', {
      reportId,
      ...quote,
    });
    return response.data;
  }

  // Preview the remediation quote for a job with no report -- the untagged-PDF
  // banner on the job status page. The from-job create charges up front, so
  // the price is confirmed with this first (OA-160).
  async previewRemediationQuoteFromJob(jobId: string): Promise<{
    quote: RemediationQuotePreview;
  }> {
    const response = await this.api.post('/remediation/preview-quote-from-job', { jobId });
    return response.data;
  }

  // Preview the remediation quote for a document URL -- the Remediate button
  // on a discovered document. The API downloads the document to price it, as
  // the from-url create does (OA-160).
  async previewRemediationQuoteFromUrl(url: string): Promise<{
    quote: RemediationQuotePreview;
  }> {
    const response = await this.api.post('/remediation/preview-quote-from-url', { url });
    return response.data;
  }

  // quote is the one the user confirmed; the API verifies quotedComplexity
  // against its own classification and answers 409 quote_mismatch when it
  // is stale (OA-160).
  async createRemediationRequestFromJob(
    jobId: string,
    quote?: { quotedComplexity?: ComplexityLevel; quotedAmount?: number }
  ): Promise<{ message: string; request: RemediationRequest }> {
    const response = await this.api.post('/remediation/from-job', { jobId, ...quote });
    return response.data;
  }

  async createRemediationRequestFromUrl(
    url: string,
    sourceUrl?: string,
    quote?: { quotedComplexity?: ComplexityLevel; quotedAmount?: number }
  ): Promise<{ message: string; request: RemediationRequest }> {
    const response = await this.api.post('/remediation/from-url', {
      url,
      sourceUrl: sourceUrl || url,
      ...quote,
    });
    return response.data;
  }

  async getMyRemediationRequests(
    filters: { limit?: number; offset?: number } = {}
  ): Promise<RemediationListResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value.toString());
      }
    });
    const response = await this.api.get(`/remediation/my-requests?${params.toString()}`);
    return response.data;
  }

  /**
   * The organization's open request for a report's or a job's document, if
   * any -- including one a colleague made (OA-278). Give exactly one id.
   */
  async getExistingRemediation(
    source: { reportId: string } | { jobId: string }
  ): Promise<{ existing: ExistingRemediation | null }> {
    const response = await this.api.get('/remediation/existing', { params: source });
    return response.data;
  }

  async getRemediationRequest(requestId: string): Promise<{ request: RemediationRequest }> {
    const response = await this.api.get(remediationPath('customer', requestId));
    return response.data;
  }

  async downloadRemediationOriginal(requestId: string): Promise<Blob> {
    return this.downloadRemediationFile('customer', requestId, 'original');
  }

  async downloadRemediationRemediated(requestId: string): Promise<Blob> {
    return this.downloadRemediationFile('customer', requestId, 'remediated');
  }

  async getRemediationComments(requestId: string): Promise<{ comments: RemediationCommentView[] }> {
    const response = await this.api.get(`${remediationPath('customer', requestId)}/comments`);
    return response.data;
  }

  async addRemediationComment(
    requestId: string,
    body: string,
    isInternal: boolean = false
  ): Promise<{ comment: RemediationCommentView }> {
    return this.postRemediationComment('customer', requestId, body, isInternal);
  }

  private async downloadRemediationFile(
    audience: RemediationAudience,
    requestId: string,
    kind: 'original' | 'remediated'
  ): Promise<Blob> {
    const response = await this.api.get(
      `${remediationPath(audience, requestId)}/download/${kind}`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  }

  private async postRemediationComment(
    audience: RemediationAudience,
    requestId: string,
    body: string,
    isInternal: boolean
  ): Promise<{ comment: RemediationCommentView }> {
    const response = await this.api.post(`${remediationPath(audience, requestId)}/comments`, {
      body,
      isInternal,
    });
    return response.data;
  }

  // Admin detail page. These go through /remediation/admin/*, which the API
  // resolves without the admin's own organisation membership, so an admin who
  // belongs to an organisation can still open any organisation's request. The
  // customer routes above scope such an admin to their own organisation and
  // answer 404 for anyone else's (OA-149).
  async getAdminRemediationRequest(requestId: string): Promise<{ request: RemediationRequest }> {
    const response = await this.api.get(remediationPath('admin', requestId));
    return response.data;
  }

  async getAdminRemediationComments(
    requestId: string
  ): Promise<{ comments: RemediationCommentView[] }> {
    const response = await this.api.get(`${remediationPath('admin', requestId)}/comments`);
    return response.data;
  }

  async addAdminRemediationComment(
    requestId: string,
    body: string,
    isInternal: boolean = false
  ): Promise<{ comment: RemediationCommentView }> {
    return this.postRemediationComment('admin', requestId, body, isInternal);
  }

  async downloadAdminRemediationOriginal(requestId: string): Promise<Blob> {
    return this.downloadRemediationFile('admin', requestId, 'original');
  }

  async downloadAdminRemediationRemediated(requestId: string): Promise<Blob> {
    return this.downloadRemediationFile('admin', requestId, 'remediated');
  }

  // Admin remediation methods
  async getAllRemediationRequests(
    filters: {
      status?: string;
      tier?: number;
      limit?: number;
      offset?: number;
      sortBy?: string;
      sortOrder?: string;
    } = {}
  ): Promise<RemediationListResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value.toString());
      }
    });
    const response = await this.api.get(`/remediation/admin/all?${params.toString()}`);
    return response.data;
  }

  async updateRemediationRequest(
    requestId: string,
    data: { status?: string; tier?: number; adminNotes?: string; assignedAdminId?: string }
  ): Promise<{ message: string; request: RemediationRequest }> {
    const response = await this.api.put(`/remediation/admin/${requestId}`, data);
    return response.data;
  }

  async uploadRemediatedFile(
    requestId: string,
    file: File
  ): Promise<{ message: string; request: RemediationRequest }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post(`/remediation/admin/${requestId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // ============================================================================
  // Admin API Methods
  // ============================================================================

  async getAdminStats(): Promise<AdminStatsResponse> {
    const response = await this.api.get('/admin/stats');
    return response.data;
  }

  async getAdminJobs(
    filters: {
      status?: 'pending' | 'processing' | 'completed' | 'failed';
      type?: string;
      priority?: string;
      userId?: string;
      limit?: number;
      offset?: number;
      sortBy?: string;
      sortOrder?: 'ASC' | 'DESC';
    } = {}
  ): Promise<AdminJobsListResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });
    const response = await this.api.get(`/admin/jobs?${params.toString()}`);
    return response.data;
  }

  async getAdminUsers(
    filters: {
      role?: string;
      status?: string;
      search?: string;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<AdminUserListResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value.toString());
      }
    });
    const response = await this.api.get(`/admin/users?${params.toString()}`);
    return response.data;
  }

  async getAdminUser(userId: string): Promise<AdminUserDetailResponse> {
    const response = await this.api.get(`/admin/users/${userId}`);
    return response.data;
  }

  async updateUserRole(
    userId: string,
    data: { role?: string; adminPermissions?: Record<string, string> | null }
  ): Promise<{ message: string; user: AdminUser }> {
    const response = await this.api.put(`/admin/users/${userId}/role`, data);
    return response.data;
  }
  async inviteAdminUser(data: {
    email: string;
    role: 'admin' | 'staff';
    adminPermissions?: Record<string, string>;
  }): Promise<{ message: string; user: { userId: string; email: string; role: string } }> {
    const response = await this.api.post('/admin/users/invite', data);
    return response.data;
  }

  /**
   * Admin grant of prepaid credits to a user's tenant (issue #392).
   */
  async grantCredits(
    userId: string,
    payload: { amount: number; workType: 'analysis' | 'remediation' | 'both'; note?: string }
  ): Promise<{
    data: {
      replenishmentId: string;
      balanceId: string;
      workType: string;
      amount: number;
      newBalance: { total: number; used: number; remaining: number };
    };
  }> {
    const response = await this.api.post(`/admin/users/${userId}/grant-credits`, payload);
    return response.data;
  }

  // Webhook management
  async getWebhooks(): Promise<any[]> {
    const response = await this.api.get('/webhooks');
    return response.data.data || [];
  }

  async createWebhook(data: {
    url: string;
    events?: string[];
    description?: string;
  }): Promise<any> {
    const response = await this.api.post('/webhooks', data);
    return response.data;
  }

  async updateWebhook(
    webhookId: string,
    data: { url?: string; events?: string[]; isActive?: boolean; description?: string }
  ): Promise<any> {
    const response = await this.api.patch(`/webhooks/${webhookId}`, data);
    return response.data;
  }

  async deleteWebhook(webhookId: string): Promise<any> {
    const response = await this.api.delete(`/webhooks/${webhookId}`);
    return response.data;
  }

  async testWebhook(webhookId: string): Promise<any> {
    const response = await this.api.post(`/webhooks/${webhookId}/test`);
    return response.data;
  }

  async rotateWebhookSecret(webhookId: string): Promise<any> {
    const response = await this.api.post(`/webhooks/${webhookId}/rotate-secret`);
    return response.data;
  }

  async getWebhookDeliveries(
    params: { webhookId?: string; limit?: number; offset?: number } = {}
  ): Promise<any> {
    const query = new URLSearchParams();
    if (params.webhookId) {
      query.append('webhookId', params.webhookId);
    }
    if (params.limit) {
      query.append('limit', params.limit.toString());
    }
    if (params.offset) {
      query.append('offset', params.offset.toString());
    }
    const response = await this.api.get(`/webhooks/deliveries?${query}`);
    return response.data;
  }

  async getDashboardKpis(): Promise<any> {
    const response = await this.api.get('/product-dashboard/kpis');
    return response.data.data || response.data;
  }

  async getDashboardTrends(): Promise<any> {
    const response = await this.api.get('/product-dashboard/trends');
    return response.data.data || response.data;
  }

  async getDashboardDiscovery(): Promise<any> {
    const response = await this.api.get('/product-dashboard/discovery');
    return response.data.data || response.data;
  }

  async getDashboardRemediation(): Promise<any> {
    const response = await this.api.get('/product-dashboard/remediation');
    return response.data.data || response.data;
  }

  async getDashboardAdoption(): Promise<any> {
    const response = await this.api.get('/product-dashboard/adoption');
    return response.data.data || response.data;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // New Billing System — Phase 1
  // ═══════════════════════════════════════════════════════════════════════

  async getPricing(): Promise<any> {
    const response = await this.api.get('/pricing');
    return response.data.data || response.data;
  }

  async getCurrentPlan(): Promise<any> {
    const response = await this.api.get('/account/plan');
    return response.data.data || response.data;
  }

  async activatePayg(): Promise<any> {
    const response = await this.api.post('/plans/payg/activate');
    return response.data.data || response.data;
  }

  async updatePaygSettings(settings: { monthlySpendingLimit?: number | null }): Promise<any> {
    const response = await this.api.patch('/plans/payg/settings', settings);
    return response.data.data || response.data;
  }

  async replenishBalance(
    amount: number,
    workType?: 'analysis' | 'remediation' | 'both'
  ): Promise<any> {
    const body: { amount: number; workType?: string } = { amount };
    if (workType && workType !== 'both') {
      body.workType = workType;
    }
    const response = await this.api.post('/plans/payg/replenishments', body);
    return response.data.data || response.data;
  }

  async getBalances(): Promise<any> {
    const response = await this.api.get('/balances');
    return response.data.data || response.data;
  }

  async getBalanceSummary(): Promise<any> {
    const response = await this.api.get('/balances/summary');
    return response.data.data || response.data;
  }

  async getBillingSummary(): Promise<any> {
    const response = await this.api.get('/billing/summary');
    return response.data.data || response.data;
  }

  async getUsageLedger(params?: { limit?: number; offset?: number }): Promise<any> {
    const query = new URLSearchParams();
    if (params?.limit) {
      query.append('limit', params.limit.toString());
    }
    if (params?.offset) {
      query.append('offset', params.offset.toString());
    }
    const response = await this.api.get(`/billing/ledger?${query}`);
    return response.data;
  }

  async getUsageSummary(params?: { startDate?: string; endDate?: string }): Promise<any> {
    const query = new URLSearchParams();
    if (params?.startDate) {
      query.append('startDate', params.startDate);
    }
    if (params?.endDate) {
      query.append('endDate', params.endDate);
    }
    const response = await this.api.get(`/billing/usage-summary?${query}`);
    return response.data.data || response.data;
  }

  async getBillingEvents(limit = 25): Promise<any> {
    const response = await this.api.get(`/billing/events?limit=${limit}`);
    return response.data.data || response.data;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // New Billing System — Phase 2 (Professional / Enterprise / Contracts)
  // ═══════════════════════════════════════════════════════════════════════

  async estimateProfessional(
    usageEstimates: Array<{
      documentFormat: string;
      estimatedMonthlyUnits: number;
    }>,
    remediationEstimates: Array<{
      documentFormat: string;
      estimatedMonthlyUnits: number;
    }> = []
  ): Promise<any> {
    const response = await this.api.post('/plans/professional/estimate', {
      usageEstimates,
      remediationEstimates,
    });
    return response.data.data || response.data;
  }

  async requestProfessional(params: {
    annualCommitmentAmount: number;
    billingPreference?: 'card' | 'invoice';
    usageEstimates?: Array<{ documentFormat: string; estimatedMonthlyUnits: number }>;
    remediationEstimates?: Array<{ documentFormat: string; estimatedMonthlyUnits: number }>;
  }): Promise<any> {
    const response = await this.api.post('/plans/professional/requests', params);
    return response.data.data || response.data;
  }

  async requestEnterprise(params: {
    notes?: string;
    usageEstimates?: Array<{ documentFormat: string; estimatedMonthlyUnits: number }>;
  }): Promise<any> {
    const response = await this.api.post('/plans/enterprise/requests', params);
    return response.data.data || response.data;
  }

  async getCurrentContract(): Promise<any> {
    const response = await this.api.get('/contracts/current');
    return response.data.data || response.data;
  }

  /**
   * Issue #394: preview the prorated cost of raising an active contract's
   * annual commitment without actually changing it.
   */
  async previewCommitmentIncrease(
    contractId: string,
    newAmount: number
  ): Promise<{
    data: {
      contractId: string;
      currentAmount: number;
      newAmount: number;
      delta: number;
      monthsRemaining: number;
      proratedAmount: number;
    };
  }> {
    const response = await this.api.get(
      `/contracts/${contractId}/increase-preview?newAmount=${encodeURIComponent(String(newAmount))}`
    );
    return response.data;
  }

  async increaseCommitment(
    contractId: string,
    newAnnualCommitmentAmount: number
  ): Promise<{
    data: {
      contractId: string;
      annualCommitmentAmount: number;
      previousAmount: number;
      delta: number;
      monthsRemaining: number;
      proratedAmount: number;
    };
  }> {
    const response = await this.api.post(`/contracts/${contractId}/increase`, {
      newAnnualCommitmentAmount,
    });
    return response.data;
  }

  async acceptContract(
    contractId: string,
    signature: {
      signerName: string;
      signerTitle?: string;
      consentAccepted: boolean;
      signatureText?: string;
    }
  ): Promise<any> {
    const response = await this.api.post(`/contracts/${contractId}/accept`, signature);
    return response.data.data || response.data;
  }

  async getRenewalPreview(contractId: string): Promise<any> {
    const response = await this.api.get(`/contracts/${contractId}/renewal-preview`);
    return response.data.data || response.data;
  }

  async requestPlanChange(toTier: string, notes?: string): Promise<any> {
    const response = await this.api.post('/plan-changes', { toTier, notes });
    return response.data.data || response.data;
  }

  async listPlanChanges(): Promise<any> {
    const response = await this.api.get('/plan-changes');
    return response.data.data || response.data;
  }

  // Remediation billing
  async getRemediationFundingSummary(remediationRequestId: string): Promise<any> {
    const response = await this.api.get(
      `/remediation/jobs/${remediationRequestId}/funding-summary`
    );
    return response.data;
  }

  async getRemediationTenantFundingSummary(): Promise<{
    openQuotes: number;
    totalQuotedAmount: number;
    authorizedPending: number;
    capturedThisPeriod: number;
    capturedAllTime: number;
    releasedAllTime: number;
  }> {
    const response = await this.api.get('/remediation/funding-summary');
    return response.data.data || response.data;
  }

  async getRemediationQuotes(): Promise<any> {
    // Get all quotes for this tenant via billing events or direct query
    // For now, uses the remediation list and enriches with quote data
    const response = await this.api.get('/billing/events?limit=100');
    return response.data.data || response.data;
  }

  // -------------------------------------------------------------------------
  // Enterprise Authentication (#219)
  // Base: /organizations — tenant-owner-gated, operates on the caller's org.
  // -------------------------------------------------------------------------

  // -- Domains
  async getOrganizationDomains(): Promise<{ domains: OrganizationDomain[] }> {
    const response = await this.api.get('/organizations/domains');
    return response.data;
  }

  async addOrganizationDomain(
    domain: string
  ): Promise<{ domain: string; dnsRecord: DomainDnsRecord }> {
    const response = await this.api.post('/organizations/domains', { domain });
    return response.data;
  }

  async verifyOrganizationDomain(
    organizationDomainId: string
  ): Promise<{ domain: string; verified: boolean; reason?: string }> {
    const response = await this.api.post(`/organizations/domains/${organizationDomainId}/verify`);
    return response.data;
  }

  async deleteOrganizationDomain(organizationDomainId: string): Promise<void> {
    await this.api.delete(`/organizations/domains/${organizationDomainId}`);
  }

  // -- Identity providers
  async getIdentityProviders(): Promise<{ identityProviders: IdentityProvider[] }> {
    const response = await this.api.get('/organizations/identity-providers');
    return response.data;
  }

  async createIdentityProvider(
    input: IdentityProviderInput
  ): Promise<{ identityProvider: IdentityProvider }> {
    const response = await this.api.post('/organizations/identity-providers', input);
    return response.data;
  }

  async updateIdentityProvider(
    identityProviderId: string,
    input: IdentityProviderInput
  ): Promise<{ identityProvider: IdentityProvider }> {
    const response = await this.api.patch(
      `/organizations/identity-providers/${identityProviderId}`,
      input
    );
    return response.data;
  }

  async deleteIdentityProvider(identityProviderId: string): Promise<void> {
    await this.api.delete(`/organizations/identity-providers/${identityProviderId}`);
  }

  async setIdentityProviderActive(
    identityProviderId: string,
    active: boolean
  ): Promise<{ identityProvider: IdentityProvider }> {
    const response = await this.api.post(
      `/organizations/identity-providers/${identityProviderId}/activate`,
      { active }
    );
    return response.data;
  }

  async testIdentityProvider(identityProviderId: string): Promise<{ authorizationUrl: string }> {
    const response = await this.api.post(
      `/organizations/identity-providers/${identityProviderId}/test`
    );
    return response.data;
  }

  // -- Auth policy
  async getAuthPolicy(): Promise<{ authPolicy: AuthPolicy }> {
    const response = await this.api.get('/organizations/auth-policy');
    return response.data;
  }

  async updateAuthPolicy(updates: Partial<AuthPolicy>): Promise<{ authPolicy: AuthPolicy }> {
    const response = await this.api.patch('/organizations/auth-policy', updates);
    return response.data;
  }

  // -- Break-glass exemptions
  async getSsoExemptions(): Promise<{ exemptions: SsoExemption[] }> {
    const response = await this.api.get('/organizations/sso-exemptions');
    return response.data;
  }

  async addSsoExemption(data: {
    email: string;
    reason?: string;
    expiresAt?: string;
  }): Promise<{ exemption: SsoExemption }> {
    const response = await this.api.post('/organizations/sso-exemptions', data);
    return response.data;
  }

  async deleteSsoExemption(ssoExemptionId: string): Promise<void> {
    await this.api.delete(`/organizations/sso-exemptions/${ssoExemptionId}`);
  }

  // -- Diagnostics / audit
  async getSsoDiagnostics(params?: {
    limit?: number;
    failuresOnly?: boolean;
  }): Promise<{ diagnostics: SsoDiagnosticEntry[] }> {
    const response = await this.api.get('/organizations/sso-diagnostics', { params });
    return response.data;
  }

  // -- Migration impact report
  async getSsoImpactReport(): Promise<{ impactReport: SsoImpactReport }> {
    const response = await this.api.get('/organizations/sso-impact-report');
    return response.data;
  }

  /** Fetch the impact report as CSV text (for a download link). */
  async getSsoImpactReportCsv(): Promise<string> {
    const response = await this.api.get('/organizations/sso-impact-report', {
      params: { format: 'csv' },
      responseType: 'text',
    });
    return response.data as string;
  }

  async sendSsoMigrationNotice(data?: {
    userIds?: string[];
    message?: string;
  }): Promise<{ notified: number; skipped: number }> {
    const response = await this.api.post('/organizations/sso-migration-notice', data ?? {});
    return response.data;
  }
}

export const apiService = new ApiService();
